import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X } from "lucide-react";

const STORAGE_KEY = "rebrand_banner_dismissed_v1";
const SESSION_KEY = "rebrand_banner_active_v1";
const OLD_DOMAIN_HINTS = ["nailsbynijah.com", "nailsbynijah"];

const RebrandBanner = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Already dismissed permanently?
    try {
      if (localStorage.getItem(STORAGE_KEY) === "true") return;
    } catch {
      // ignore storage errors
    }

    let active = false;

    // Persist within this browser session once detected so the banner stays
    // even after they navigate inside the new site.
    try {
      if (sessionStorage.getItem(SESSION_KEY) === "true") {
        active = true;
      }
    } catch {
      // ignore
    }

    if (!active) {
      const ref = document.referrer || "";
      const params = new URLSearchParams(window.location.search);
      const fromParam = (params.get("from") || params.get("ref") || "").toLowerCase();

      const fromOldDomain =
        OLD_DOMAIN_HINTS.some((h) => ref.toLowerCase().includes(h)) ||
        OLD_DOMAIN_HINTS.some((h) => fromParam.includes(h)) ||
        fromParam === "nbn";

      if (fromOldDomain) {
        active = true;
        try {
          sessionStorage.setItem(SESSION_KEY, "true");
        } catch {
          // ignore
        }
      }
    }

    if (active) setShow(true);
  }, []);

  const dismiss = () => {
    setShow(false);
    try {
      localStorage.setItem(STORAGE_KEY, "true");
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      // ignore
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="relative overflow-hidden border-b border-primary/30"
          role="status"
          aria-live="polite"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[hsl(40_90%_28%)] via-primary to-[hsl(40_90%_28%)]" />
          <div
            className="absolute inset-0 opacity-30"
            style={{
              background:
                "linear-gradient(105deg, transparent 20%, hsl(40 80% 70% / 0.5) 45%, hsl(40 100% 85% / 0.7) 50%, hsl(40 80% 70% / 0.5) 55%, transparent 80%)",
              backgroundSize: "300% 100%",
              animation: "bannerShimmer 3s ease-in-out infinite",
            }}
          />

          <div className="relative z-10 container mx-auto px-6 py-3 sm:py-4 flex items-center justify-center gap-3 text-center">
            <Sparkles
              size={16}
              className="text-[hsl(40_100%_85%)] shrink-0 drop-shadow-[0_0_6px_hsl(40_80%_60%/0.8)]"
            />
            <p className="text-primary-foreground text-xs sm:text-sm font-medium tracking-wide drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
              <span className="font-serif italic font-bold">nailsbynijah.com</span>{" "}
              is now{" "}
              <span className="font-serif italic font-bold">inparadisebeauty.com</span>
              <span className="hidden sm:inline">
                {" "}
                — we hope you enjoy the new experience.
              </span>
            </p>
            <Sparkles
              size={16}
              className="text-[hsl(40_100%_85%)] shrink-0 hidden sm:block drop-shadow-[0_0_6px_hsl(40_80%_60%/0.8)]"
            />
          </div>

          <button
            onClick={dismiss}
            className="absolute right-3 top-1/2 -translate-y-1/2 z-20 p-1.5 rounded-full hover:bg-primary-foreground/20 transition-colors text-primary-foreground/80 hover:text-primary-foreground"
            aria-label="Dismiss banner"
          >
            <X size={16} />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RebrandBanner;
